import React, { useState, useEffect } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Calendar,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { RestaurantStats } from '../../types';
import apiService from '../../services/api';

const StatsPage: React.FC = () => {
  const { restaurant } = useRestaurant();
  const [stats, setStats] = useState<RestaurantStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (restaurant) {
      fetchStats();
    }
  }, [restaurant, period]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStats = async () => {
    if (!restaurant) return;

    try {
      setIsLoading(true);
      const data = await apiService.getRestaurantStats(restaurant.id, period);
      setStats(data);
    } catch (error: any) {
      console.error('Failed to fetch stats:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        url: error?.config?.url
      });
    } finally {
      setIsLoading(false);
    }
  };

  const periodOptions = [
    { value: 7, label: 'Últimos 7 dias' },
    { value: 30, label: 'Últimos 30 dias' },
    { value: 90, label: 'Últimos 3 meses' },
    { value: 365, label: 'Último ano' },
  ];

  const statCards = [
    {
      title: 'Total de Reservas',
      value: stats?.total_reservations || 0,
      icon: Calendar,
      color: 'bg-blue-500',
      description: `Reservas nos últimos ${period} dias`,
    },
    {
      title: 'Reservas Confirmadas',
      value: (stats?.reservations_by_status?.confirmada || 0) + (stats?.reservations_by_status?.concluida || 0),
      icon: Users,
      color: 'bg-green-500',
      description: 'Reservas confirmadas e concluídas',
    },
    {
      title: 'Reservas Pendentes',
      value: stats?.reservations_by_status?.pendente || 0,
      icon: Calendar,
      color: 'bg-yellow-500',
      description: 'Reservas aguardando confirmação',
    },
    {
      title: 'Próximas Reservas',
      value: stats?.upcoming_reservations || 0,
      icon: TrendingUp,
      color: 'bg-purple-500',
      description: 'Reservas futuras confirmadas',
    },
  ];

  const getStatusColor = (status: string) => {
    if (!stats) return 'text-secondary-500';
    
    const total = stats.total_reservations;
    const confirmed = (stats.reservations_by_status?.confirmada || 0) + (stats.reservations_by_status?.concluida || 0);
    const cancelled = stats.reservations_by_status?.cancelada || 0;
    
    if (total === 0) return 'text-secondary-500';
    if (confirmed / total > 0.8) return 'text-green-600';
    if (cancelled / total > 0.3) return 'text-red-600';
    return 'text-yellow-600';
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-primary-600 mx-auto" />
          <p className="mt-2 text-sm text-secondary-600">A carregar estatísticas...</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-sm text-red-600">Restaurante não encontrado</p>
          <p className="text-xs text-secondary-500 mt-1">Verifique se está logado com um restaurante válido</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold leading-7 text-secondary-900 sm:truncate sm:text-3xl">
            Estatísticas
          </h1>
          <p className="mt-1 text-sm text-secondary-500">
            Análise de desempenho do restaurante
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:ml-4 md:mt-0">
          <select
            value={period}
            onChange={(e) => setPeriod(parseInt(e.target.value))}
            className="input-field"
          >
            {periodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            onClick={fetchStats}
            disabled={isLoading}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </button>
        </div>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat, index) => (
          <div key={index} className="card p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-secondary-500 truncate">
                    {stat.title}
                  </dt>
                  <dd className="text-lg font-semibold text-secondary-900">
                    {stat.value}
                  </dd>
                  <dd className="text-xs text-secondary-500">
                    {stat.description}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Stats */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Reservation Breakdown */}
          <div className="card p-6">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">
              Breakdown de Reservas
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600">Total de Reservas</span>
                <span className="font-semibold">{stats.total_reservations}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600">Confirmadas</span>
                <span className="font-semibold text-green-600">{stats.reservations_by_status?.confirmada || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600">Concluídas</span>
                <span className="font-semibold text-blue-600">{stats.reservations_by_status?.concluida || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600">Pendentes</span>
                <span className="font-semibold text-yellow-600">{stats.reservations_by_status?.pendente || 0}</span>
              </div>
              {stats.reservations_by_status?.cancelada && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-secondary-600">Canceladas</span>
                  <span className="font-semibold text-red-600">{stats.reservations_by_status.cancelada}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600">Taxa de Confirmação</span>
                <span className={`font-semibold ${getStatusColor('confirmed')}`}>
                  {stats.total_reservations > 0 
                    ? `${(((stats.reservations_by_status?.confirmada || 0) + (stats.reservations_by_status?.concluida || 0)) / stats.total_reservations * 100).toFixed(1)}%`
                    : '0%'
                  }
                </span>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="card p-6">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">
              Métricas de Desempenho
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600">Período Analisado</span>
                <span className="font-semibold text-purple-600">{stats.period_days} dias</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600">Próximas Reservas</span>
                <span className="font-semibold text-blue-600">{stats.upcoming_reservations}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600">Média Diária</span>
                <span className="font-semibold">
                  {(stats.total_reservations / stats.period_days).toFixed(1)} reservas/dia
                </span>
              </div>
              {stats.taxa_ocupacao !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-secondary-600">Taxa de Ocupação</span>
                  <span className="font-semibold text-purple-600">{stats.taxa_ocupacao.toFixed(1)}%</span>
                </div>
              )}
              {stats.clientes_unicos !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-secondary-600">Clientes Únicos</span>
                  <span className="font-semibold">{stats.clientes_unicos}</span>
                </div>
              )}
              {stats.mesa_mais_popular && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-secondary-600">Mesa Mais Popular</span>
                  <span className="font-semibold">{stats.mesa_mais_popular}</span>
                </div>
              )}
              {stats.horario_pico && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-secondary-600">Horário Pico</span>
                  <span className="font-semibold">{stats.horario_pico}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Additional Details Toggle */}
      <div className="card p-6">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="w-full flex items-center justify-between text-left"
        >
          <h3 className="text-lg font-medium text-secondary-900">
            Detalhes Adicionais
          </h3>
          {showDetails ? (
            <ChevronUp className="h-5 w-5 text-secondary-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-secondary-400" />
          )}
        </button>
        
        {showDetails && stats && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-secondary-700 mb-2">Análise Temporal</h4>
              <p className="text-sm text-secondary-600 mb-2">
                <strong>Período analisado:</strong> {stats.period_days} dias
              </p>
              <p className="text-sm text-secondary-600 mb-2">
                <strong>Média diária:</strong> {(stats.total_reservations / stats.period_days).toFixed(1)} reservas
              </p>
              <p className="text-sm text-secondary-600 mb-2">
                <strong>Próximas reservas:</strong> {stats.upcoming_reservations}
              </p>
              {stats.horario_pico && (
                <p className="text-sm text-secondary-600">
                  <strong>Horário de maior movimento:</strong> {stats.horario_pico}
                </p>
              )}
            </div>
            
            <div>
              <h4 className="font-medium text-secondary-700 mb-2">Insights</h4>
              <div className="space-y-1 text-sm text-secondary-600">
                {stats.taxa_ocupacao && stats.taxa_ocupacao > 80 && (
                  <p className="text-green-600">✓ Excelente taxa de ocupação</p>
                )}
                {stats.taxa_ocupacao && stats.taxa_ocupacao < 50 && (
                  <p className="text-yellow-600">⚠ Taxa de ocupação pode ser melhorada</p>
                )}
                {stats.reservations_by_status?.cancelada && (stats.reservations_by_status.cancelada / stats.total_reservations) > 0.3 && (
                  <p className="text-red-600">⚠ Taxa de cancelamento elevada</p>
                )}
                {stats.upcoming_reservations > stats.total_reservations * 0.5 && (
                  <p className="text-blue-600">ℹ Boa agenda de reservas futuras</p>
                )}
                {(stats.reservations_by_status?.pendente || 0) > stats.total_reservations * 0.7 && (
                  <p className="text-yellow-600">⚠ Muitas reservas pendentes de confirmação</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Visual Chart Placeholder */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-secondary-900 mb-4 flex items-center space-x-2">
          <BarChart3 className="h-5 w-5" />
          <span>Gráfico de Reservas</span>
        </h3>
        <div className="bg-secondary-50 rounded-lg p-8 text-center">
          <BarChart3 className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
          <p className="text-secondary-600">
            Gráfico visual será implementado numa versão futura
          </p>
        </div>
      </div>
    </div>
  );
};

export default StatsPage;
